import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { compressImage } from '../../lib/image';
import { X, Copy, Check, Upload } from 'lucide-react';
import { PaymentLogo } from '../common/BrandLogo';

interface DepositModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialMethod?: 'kpay' | 'wavepay';
}

export const DepositModal: React.FC<DepositModalProps> = ({ onClose, onSuccess, initialMethod = 'kpay' }) => {
  const { t } = useLanguage();
  const { isGuest } = useAuth();
  const [amount, setAmount] = useState('10000');
  const [paymentMethod, setPaymentMethod] = useState<'kpay' | 'wavepay'>(initialMethod);
  const [txRef, setTxRef] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [receivingAccounts, setReceivingAccounts] = useState<any[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    api.getReceivingAccounts().then((accs) => {
      if (accs && accs.length > 0) {
        setReceivingAccounts(accs);
      }
    }).catch(() => {});
  }, []);

  const activeAccount = receivingAccounts.find(
    (a) => a.type === 'payment' && (paymentMethod === 'kpay' ? (a.provider.toLowerCase().includes('kpay') || a.id.includes('kpay')) : (a.provider.toLowerCase().includes('wave') || a.id.includes('wave')))
  );
  const displayNumber = activeAccount?.account_number || '09987654321';
  const displayName = activeAccount?.account_name || (paymentMethod === 'kpay' ? 'PluvP Shop (U Tin Tun)' : 'PluvP Shop (WavePay)');

  const handleCopyAccount = (acc: string) => {
    navigator.clipboard.writeText(acc);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('ကျေးဇူးပြု၍ ဓာတ်ပုံ သို့မဟုတ် Screenshot သာ ရွေးချယ်ပေးပါ');
      return;
    }

    setCompressing(true);
    setError('');
    try {
      const dataUrl = await compressImage(file);
      setSlipUrl(dataUrl);
    } catch {
      setError('ပုံရိပ် တင်သွင်းမှု မအောင်မြင်ပါ။ နောက်တစ်ကြိမ် ကြိုးစားပေးပါ။');
    } finally {
      setCompressing(false);
    }
  };

  const handleDeposit = async () => {
    setError('');
    const amt = parseInt(amount.replace(/\D/g, ''), 10);
    if (!amt || amt < 1000) {
      setError('အနည်းဆုံး ၁,၀၀၀ ကျပ် သွင်းပေးရပါမည်။');
      return;
    }

    if (!slipUrl) {
      setError('ကျေးဇူးပြု၍ ငွေလွှဲပြေစာ Screenshot ထည့်သွင်းပေးပါ (Payment screenshot is required)');
      return;
    }

    setLoading(true);
    try {
      await api.createDeposit(amt, paymentMethod, txRef, slipUrl);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Deposit submission failed');
    } finally {
      setLoading(false);
    }
  };

  const presetAmounts = [5000, 10000, 20000, 50000];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-surface-card w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-plum-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-5">
        <div className="px-5 py-4 border-b border-plum-border flex items-center justify-between">
          <h2 className="font-bold text-plum-dark text-base">{t.deposit}</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-page text-plum-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {error && (
            <div className="p-3 bg-red-500/15 border border-red-500/30 rounded-xl text-xs text-red-400">
              {error}
            </div>
          )}

          {isGuest && (
            <div className="p-3 bg-surface-accent rounded-xl border border-primary/20 flex items-center justify-between text-xs">
              <span className="text-plum-muted">💡 အကောင့်ကို Telegram နှင့် ချိတ်ဆက်လိုပါက</span>
              <a
                href="https://t.me/pluvp_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-bold hover:underline"
              >
                Bot တွင်ဖွင့်ပါ
              </a>
            </div>
          )}

          {/* Amount input */}
          <div>
            <label className="block text-xs font-semibold text-plum-dark mb-1.5">
              သွင်းမည့် ငွေပမာဏ (Ks)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-3 text-lg text-plum-dark font-bold font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <div className="flex space-x-2 mt-2">
              {presetAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amt.toString())}
                  className="flex-1 py-1.5 text-xs font-semibold rounded-lg border border-plum-border bg-surface-page hover:border-primary hover:text-primary transition"
                >
                  {amt.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label className="block text-xs font-semibold text-plum-dark mb-2">
              ငွေလွှဲမည့် နည်းလမ်း ရွေးပါ
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {/* KBZPay Card */}
              <button
                type="button"
                onClick={() => setPaymentMethod('kpay')}
                className={`p-3 rounded-2xl border text-left transition-all relative flex items-center space-x-3 touch-target ${
                  paymentMethod === 'kpay'
                    ? 'border-blue-500 bg-blue-50/30 ring-2 ring-blue-400/30 shadow-xs'
                    : 'border-plum-border bg-surface-page hover:border-plum-border/80'
                } active:scale-[0.98]`}
              >
                <PaymentLogo method="kpay" size="md" className="rounded-xl shadow-xs" />
                <div className="flex flex-col flex-1">
                  <span className="text-xs font-bold text-plum-dark">KBZPay</span>
                  <span className="text-[10px] text-plum-muted">KPay အကောင့်</span>
                </div>
                {paymentMethod === 'kpay' && (
                  <div className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </button>

              {/* WavePay Card */}
              <button
                type="button"
                onClick={() => setPaymentMethod('wavepay')}
                className={`p-3 rounded-2xl border text-left transition-all relative flex items-center space-x-3 touch-target ${
                  paymentMethod === 'wavepay'
                    ? 'border-amber-500 bg-amber-50/30 ring-2 ring-amber-400/30 shadow-xs'
                    : 'border-plum-border bg-surface-page hover:border-plum-border/80'
                } active:scale-[0.98]`}
              >
                <PaymentLogo method="wavepay" size="md" className="rounded-xl shadow-xs" />
                <div className="flex flex-col flex-1">
                  <span className="text-xs font-bold text-plum-dark">WavePay</span>
                  <span className="text-[10px] text-plum-muted">Wave Money</span>
                </div>
                {paymentMethod === 'wavepay' && (
                  <div className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Receiving account info card */}
          <div className="p-3.5 bg-surface-accent rounded-2xl border border-primary/20 space-y-2.5">
            <div className="flex items-center space-x-2 pb-1.5 border-b border-primary/10">
              <PaymentLogo method={paymentMethod} size="xs" className="rounded-md" />
              <span className="text-xs font-bold text-plum-dark">
                {paymentMethod === 'kpay' ? 'KBZPay' : 'WavePay'} လက်ခံအကောင့် အချက်အလက်
              </span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-plum-muted">လက်ခံသူအမည်:</span>
              <span className="font-semibold text-plum-dark">{displayName}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-plum-muted">လက်ခံဖုန်းနံပါတ်:</span>
              <div className="flex items-center space-x-2">
                <span className="font-mono font-bold text-sm text-plum-dark tracking-wide">{displayNumber}</span>
                <button
                  type="button"
                  onClick={() => handleCopyAccount(displayNumber)}
                  className="px-2.5 py-1 rounded-lg bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary-light hover:text-white transition flex items-center space-x-1 text-xs font-semibold active:scale-95"
                  title="Copy account number"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-status-green" />
                      <span className="text-status-green font-bold text-[11px]">ကူးပြီး!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-primary-light" />
                      <span className="text-primary-light font-semibold text-[11px]">Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Payment Slip / Screenshot Upload */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-plum-dark">
                ငွေလွှဲပြေစာ Screenshot (Payment Slip)
              </label>
              <span className="text-[10px] text-amber-400 font-bold bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                * လိုအပ်ပါသည်
              </span>
            </div>

            {slipUrl ? (
              /* Image Uploaded Preview */
              <div className="relative rounded-2xl border border-primary/40 bg-surface-page p-3 flex items-center space-x-3">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-black border border-plum-border shrink-0 flex items-center justify-center">
                  <img src={slipUrl} alt="Slip Preview" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1 text-xs font-bold text-status-green">
                    <Check className="w-4 h-4" />
                    <span>Screenshot တင်ပြီးပါပြီ</span>
                  </div>
                  <p className="text-[11px] text-plum-muted mt-0.5 truncate">
                    ပြေစာ စစ်ဆေးပြီး အတည်ပြုပါမည်
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSlipUrl('');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="p-1.5 rounded-lg bg-surface-accent hover:bg-red-500/20 text-plum-muted hover:text-status-red transition shrink-0"
                  title="Remove screenshot"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              /* Empty Upload Box */
              <label
                className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-4 cursor-pointer transition text-center ${
                  compressing
                    ? 'border-primary bg-primary/5'
                    : 'border-plum-border hover:border-primary/50 bg-surface-page hover:bg-surface-accent/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={compressing}
                  className="sr-only"
                />
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary mb-2">
                  <Upload className={`w-5 h-5 ${compressing ? 'animate-bounce' : ''}`} />
                </div>
                <span className="text-xs font-bold text-plum-dark">
                  {compressing ? 'ပုံရိပ် ချုံ့နေပါသည်...' : 'ငွေလွှဲပြေစာ Screenshot တင်ရန် နှိပ်ပါ'}
                </span>
                <span className="text-[10px] text-plum-muted mt-0.5">
                  KPay / WavePay ငွေလွှဲအောင်မြင်သည့် Screenshot (JPG, PNG)
                </span>
              </label>
            )}
          </div>

          {/* Transaction Ref */}
          <div>
            <label className="block text-xs font-semibold text-plum-dark mb-1">
              Transaction ID (နောက်ဆုံး ၆ လုံး - စိတ်ကြိုက်)
            </label>
            <input
              type="text"
              value={txRef}
              onChange={(e) => setTxRef(e.target.value)}
              placeholder="ဥပမာ: 881294"
              className="w-full bg-surface-page border border-plum-border rounded-xl px-3 py-2.5 text-sm text-plum-dark font-mono"
            />
          </div>

          {/* Submit */}
          <button
            type="button"
            disabled={loading || compressing}
            onClick={handleDeposit}
            className="w-full bg-primary hover:bg-primary-dark disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition shadow-xs touch-target text-sm mt-2"
          >
            {loading ? 'တင်သွင်းနေပါသည်...' : 'ငွေသွင်းလွှာ တင်မည်'}
          </button>
        </div>
      </div>
    </div>
  );
};
